import dayjs from 'dayjs';
import Commons from '../../../../../Services/Common/Common';
import { FACILITY_OTHER_CATEGORY_VALUE } from './hooks/useItemSelection';
import {
  NormalizeRsGubunForForm,
  NormalizeRsMethodForForm,
} from '../Step00_Regulation/Regulation_Schema';

const ToNumOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const ToSriId = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** TB_SF_RISK_GONGJONG_MACHINE.MACHINE_TYPE — 짧은 구분코드만 허용. UI용 categoryCd(__MANUAL_OTHER__)는 제외 */
const NormalizeMachineTypeForRiskSave = (m) => {
  const cat = m?.categoryCd;
  const type = m?.machineType;
  if (cat === FACILITY_OTHER_CATEGORY_VALUE) {
    const g = String(type ?? '4').trim();
    const coerced = g === '04' ? '4' : g;
    return coerced.slice(0, 8) || '4';
  }
  const v = String(type ?? cat ?? '01').trim();
  if (v === FACILITY_OTHER_CATEGORY_VALUE) return '4';
  return v.slice(0, 8) || '01';
};

const MapSaveItem = (p, index) => {
  const equipmentList = Array.isArray(p?.equipmentList) ? p.equipmentList : [];
  const materialList = Array.isArray(p?.materialList) ? p.materialList : [];
  return {
    SriId: ToSriId(p?.sriId),
    No: Number(p?.processNo ?? index + 1),
    GongJongCd: String(p?.processId ?? p?.gongJongCd ?? ''),
    GongJongNm: String(p?.processNm ?? p?.gongJongNm ?? ''),
    AccidentCnt: ToNumOrNull(p?.accidentCnt),
    NearMissCnt: ToNumOrNull(p?.nearMissCnt),
    WorkerTrait: p?.workerTrait ?? '',
    ShiftType: p?.shiftType ?? 'N',
    HandlingWeight: ToNumOrNull(p?.handlingWeight),
    HandlingForm: p?.handlingForm ?? '',
    TransportMethod: p?.transportMethod ?? '',
    HealthExamYn: p?.healthExamYn ?? 'Y',
    IsSafetyWorkPermit: p?.isSafetyWorkPermit ?? 'N',
    IsEnvironmentMeasurement: p?.isEnvironmentMeasurement ?? '0',
    EnvironmentMeasurement: ToNumOrNull(p?.environmentMeasurement),
    ExposureStandards: ToNumOrNull(p?.exposureStandards),
    IsSpecialSafetyTraining: p?.isSpecialSafetyTraining ?? '',
    ShiftTypeTxt: ToNumOrNull(p?.shiftTypeTxt),
    TransportMethodTxt: p?.transportMethodTxt ?? '',
    MaxRiskScore: ToNumOrNull(p?.maxRiskScore),
    MaxRiskGrade: p?.maxRiskGrade ?? '',
    Machines: equipmentList
      .map((m) => ({
        MachineId: String(m?.id ?? m?.machineId ?? m?.chaebun ?? ''),
        MachineType: NormalizeMachineTypeForRiskSave(m),
        MachineNm: String(m?.name ?? m?.machineNm ?? m?.machineName ?? ''),
      }))
      .filter((m) => m.MachineId !== ''),
    Materials: materialList
      .map((m) => ({
        HbmId: ToNumOrNull(m?.hbmId ?? m?.productId),
        HbdId: ToNumOrNull(m?.id ?? m?.hbdId ?? m?.materialId),
      }))
      .filter((m) => m.HbmId != null && m.HbdId != null),
  };
};

export const SafetyInfo_Schema = {
  fields: {
    rsNm: { initial: '', label: '위험성평가명' },
    rsGubun: { initial: '최초평가', label: '평가구분' },
    rsMethod: { initial: '위험성 수준 3단계 판단법', label: '평가방법' },
    depNm: { initial: '', label: '평가 부서' },
    rsStartDate: {
      initial: dayjs().format('YYYY-MM-DD'),
      label: '평가 시작일',
    },
    rsEndDate: { initial: dayjs().format('YYYY-MM-DD'), label: '평가 종료일' },
    processList: { initial: [], label: '공정 목록' },
    equipmentList: { initial: [], label: '사용 설비 목록' },
    materialList: { initial: [], label: '취급 물질 목록' },
  },
  mapper: {
    /**
     * GetRisCustom01R1 — [FromBody] GetRisCustom00R1Request
     * RsCd, SearchText(AES). 조회 전용 검색어 없으면 빈 문자열.
     */
    search1: {
      RsCd: (_, context) => {
        const n = Number(context.rsCd);
        return Number.isFinite(n) && n > 0 ? n : 0;
      },
      SearchText: (_, context) =>
        Commons.EncryptAES(String(context.searchText ?? '').trim()),
    },
    /**
     * 공정 선택 모달 — 부서(GetDepLast) + 공정(TB_BS_GONGJONG) 통합 (GetRisCustom01R2)
     */
    search2: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      SearchText: (_, context) =>
        Commons.EncryptAES(String(context.search?.searchText ?? '').trim()),
    },
    /** 설비 선택 모달 — 안전보건정보 통합 설비·제품 (getRisBasic01R4) */
    search3: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      Year: (_, context) =>
        Commons.EncryptAES(
          dayjs(
            context.search?.rsEndDate ?? context.search?.endDate ?? ''
          ).format('YYYY') || ''
        ),
    },
    /**
     * 안전보건정보 전체 저장 공정/설비/물질 저장 (SaveRisCustom01U2)
     */
    save1: {
      RsCd: (row, context) => Number(row?.rsCd ?? context.rsCd ?? 0),
      UserId: (_, context) => context.userInfo?.userid,
      StepSeq: (row) => {
        const n = Number(row?.stepSeq);
        return Number.isFinite(n) && n > 0 ? n : 2;
      },
      Items: (row) => {
        const processList = Array.isArray(row?.processList)
          ? row.processList
          : [];
        return processList.map((p, index) => MapSaveItem(p, index));
      },
    },
    /**
     * 공정 선택 모달 - 공정 등록 저장 (SaveRisCustom01U1)
     */
    save2: {
      CustCd: (_, context) =>
        Commons.EncryptAES(String(context.search?.custCd || '')) || '',
      DepCd: (row) => Commons.EncryptAES(String(row?.depCd || '')) || '',
      GongJongNm: (row) =>
        Commons.EncryptAES(String(row?.gongJongNm || '')) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    /** Step01 공정명 변경 — UpdateRisCustomGongJongNm */
    save5: {
      GongJongCd: (row) =>
        Commons.EncryptAES(String(row?.gongJongCd || row?.processId || '')) ||
        '',
      GongJongNm: (row) =>
        Commons.EncryptAES(
          String(row?.gongJongNm || row?.processNm || '').trim()
        ) || '',
    },
    /**
     * 설비 수기 저장 SaveRisCustom01U3 — SaveRisCustom01U3Info 목록
     * 서버: Chaebun·CustCd·Gubun·MachineName·UserId 전부 DecryptAES 후 처리.
     * 신규: Chaebun 빈값/미전송 → 서버 GetCustomSeq + INSERT. 기존 CHAEBUN 존재 시 UPDATE.
     */
    save3: {
      Chaebun: (row) => {
        const chaebun = row?.chaebun;
        if (chaebun == null) return undefined;
        const s = String(chaebun).trim();
        if (s === '' || s === '0') return undefined;
        return Commons.EncryptAES(s) || '';
      },
      CustCd: (_, context) =>
        Commons.EncryptAES(String(context.search?.custCd || '')) || '',
      Gubun: () => Commons.EncryptAES('4') || '',
      MachineName: (row) =>
        Commons.EncryptAES(String(row?.machineName ?? '').trim()) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    /**
     * TB_BO_HAZ_BASIC_04 삭제 DeleteRisCustom01D3 — DeleteRisCustom01D3Info 목록
     * 서버는 Chaebun만 사용 (복호화 후 Repository 삭제).
     */
    delete1: {
      Chaebun: (row) => {
        const s = String(row?.chaebun ?? '').trim();
        if (!s) return '';
        return Commons.EncryptAES(s) || '';
      },
    },
    /**
     * 물질 수기 저장 SaveRisCustom01U4 — SaveRisCustom01U4Info 목록
     */
    save4: {
      HbdId: (row) => {
        const hbdId = row?.hbdId ?? row?.id;
        if (hbdId == null) return undefined;
        const s = String(hbdId).trim();
        if (s === '' || s === '0') return undefined;
        return Commons.EncryptAES(s) || '';
      },
      CustCd: (_, context) =>
        Commons.EncryptAES(String(context.search?.custCd || '')) || '',
      ProductName: (row) =>
        Commons.EncryptAES(
          String(row?.productName || '수기 등록 물질').trim()
        ) || '',
      SubstanceName: (row) =>
        Commons.EncryptAES(String(row?.substanceName ?? row?.name ?? '').trim()) ||
        '',
      CasNo: (row) => Commons.EncryptAES(String(row?.casNo ?? '').trim()) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    /**
     * TB_BO_HAM_BASIC_04 삭제 DeleteRisCustom01D4 — DeleteRisCustom01D4Info 목록
     */
    delete2: {
      HbdId: (row) => {
        const s = String(row?.hbdId ?? row?.id ?? '').trim();
        if (!s) return '';
        return Commons.EncryptAES(s) || '';
      },
    },
  },
};

/** 설비 분류 (useItemSelection / ItemSelectModal facility) */
export const SAFETY_INFO_FACILITY_CATEGORIES = [
  { value: '0', label: '안전검사' },
  { value: '1', label: '안전인증 자율 안전확인신고' },
];

/**
 * 모달에서 저장분 복원(loadFromSavedData) 시 — 훅이 append하는「기타」와 동일 목록이어야 분류·체크 일치
 */
export const SAFETY_INFO_FACILITY_CATEGORIES_WITH_OTHER = [
  ...SAFETY_INFO_FACILITY_CATEGORIES,
  { value: FACILITY_OTHER_CATEGORY_VALUE, label: '기타' },
];

/** 물질 수기 등록 전용 제품 분류 ID */
export const MATERIAL_OTHER_CATEGORY_VALUE = '__MATERIAL_OTHER__';
export const MANUAL_MATERIAL_PRODUCT_ID = 900000000;

/** API 한 행에서 camelCase / PascalCase 모두 허용 */
const P = (row, camel, pascal) =>
  row?.[camel] !== undefined && row?.[camel] !== null
    ? row[camel]
    : row?.[pascal];

/** 부서코드 키 정규화 (공정 맵 / 부서 목록 일치용) */
export const NormalizeDepCdKey = (k) =>
  k == null || k === '' ? '' : String(k).trim();

/**
 * processDataByDept에서 부서코드에 해당하는 공정 배열 (키 불일치 시 완화 조회)
 */
export const GetProcessesForDepartment = (processDataByDept, depKey) => {
  const norm = NormalizeDepCdKey(depKey);
  if (!norm || !processDataByDept || typeof processDataByDept !== 'object') {
    return [];
  }
  if (Array.isArray(processDataByDept[norm])) {
    return processDataByDept[norm];
  }
  const found = Object.keys(processDataByDept).find(
    (key) => NormalizeDepCdKey(key) === norm
  );
  return found ? processDataByDept[found] || [] : [];
};

/** TB_BS_GONGJONG 등: camel/Pascal/snake/DB 관용 필드명 수용 */
const FirstString = (...candidates) => {
  for (const v of candidates) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
};

/** GetRisCustom01R3 OtherEquipment(TB_BO_HAZ_BASIC_04) → 설비 선택 모달 행 (0/1/2 분류와 동일 필드 패턴) */
export const MapOtherEquipmentToFacilityRows = (list) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((item) => {
    const chaebun = FirstString(P(item, 'chaebun', 'Chaebun'), item?.CHAEBUN);
    const machineName = FirstString(
      P(item, 'machineName', 'MachineName'),
      item?.MACHINE_NAME
    );
    const gubun = FirstString(P(item, 'gubun', 'Gubun'), item?.GUBUN) || '4';
    const machineTypeForRisk = gubun === '04' ? '4' : gubun;
    return {
      chaebun,
      machineName,
      machineNm: machineName,
      facilityCd: chaebun,
      facilityNm: machineName,
      categoryCd: FACILITY_OTHER_CATEGORY_VALUE,
      categoryNm: '기타',
      machineType: machineTypeForRisk,
      gubun: P(item, 'gubun', 'Gubun') || gubun,
      custCd: P(item, 'custCd', 'CustCd'),
    };
  });
};

/** GetRisCustom01R3 OtherMaterial(TB_BO_HAM_BASIC_04) → 물질 선택 모달 행 */
export const MapOtherMaterialToRows = (list) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((item) => {
    const hbdId = FirstString(P(item, 'hbdId', 'HbdId'));
    const hbmRaw = FirstString(P(item, 'hbmId', 'HbmId'));
    const hbmNum = Number(hbmRaw);
    // 과거 데이터(-4 등)도 수기 물질 분류를 하나로 보이도록 고정
    const hbmId =
      Number.isFinite(hbmNum) && hbmNum > 0
        ? String(hbmNum)
        : String(MANUAL_MATERIAL_PRODUCT_ID);
    const productName =
      FirstString(P(item, 'productName', 'ProductName')) || '수기 등록 물질';
    const substanceName = FirstString(
      P(item, 'substanceName', 'SubstanceName'),
      P(item, 'materialName', 'MaterialName')
    );
    const casNo = FirstString(P(item, 'casNo', 'CasNo'), item?.CAS_NO);
    return {
      hbdId,
      hbmId,
      productId: hbmId,
      materialId: hbdId,
      productName,
      substanceName,
      materialName: substanceName,
      materialNm: substanceName,
      casNo,
      CAS_NO: casNo,
      isManual: true,
      categoryCd: MATERIAL_OTHER_CATEGORY_VALUE,
      categoryNm: '수기 등록',
    };
  });
};

export const MapGongjongRowToProcessItem = (g) => {
  if (!g || typeof g !== 'object') return null;
  const depCd = NormalizeDepCdKey(
    FirstString(P(g, 'depCd', 'DepCd'), g.DEP_CD, g.dep_cd, g.DepartmentCode)
  );
  if (!depCd) return null;
  const gongJongCd = FirstString(
    P(g, 'gongJongCd', 'GongJongCd'),
    g.GONGJONG_CD,
    g.GongjongCd,
    g.gongjongCd,
    g.GONGJONGCD
  );
  const gongJongNm = FirstString(
    P(g, 'gongJongNm', 'GongJongNm'),
    g.GONGJONG_NM,
    g.GongjongNm,
    g.gongjongNm,
    P(g, 'gongJongName', 'GongJongName'),
    g.GONGJONG_NAME,
    g.ProcessName,
    g.processNm
  );
  const depNm = FirstString(
    P(g, 'depNm', 'DepNm'),
    g.DEP_NM,
    g.dep_nm,
    g.DepartmentName
  );
  return { depCd, gongJongCd, gongJongNm, depNm };
};

/** 모달·선택 토글 시 동일 공정 행 판별 (코드가 비어 있으면 명칭으로 구분) */
export const ProcessesMatchForSelection = (a, b) => {
  if (!a || !b) return false;
  if (NormalizeDepCdKey(a.depCd) !== NormalizeDepCdKey(b.depCd)) return false;
  const ca = String(a.gongJongCd ?? '').trim();
  const cb = String(b.gongJongCd ?? '').trim();
  if (ca !== cb) return false;
  if (ca === '' && cb === '') {
    return (
      String(a.gongJongNm ?? '').trim() === String(b.gongJongNm ?? '').trim()
    );
  }
  return true;
};

/** 목록·카드에 표시할 공정명 (필드 누락 시 코드 등 대체) */
export const GetProcessDisplayName = (proc) => {
  if (!proc) return '(공정명 없음)';
  return (
    FirstString(
      proc.gongJongNm,
      proc.processNm,
      proc.label,
      proc.gongJongCd,
      proc.processId,
      proc.value
    ) || '(공정명 없음)'
  );
};

/** RsStartDate / RsEndDate 등 → TextField type="date"용 YYYY-MM-DD */
const NormalizeRsDateForInput = (value) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (!s) return '';
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

/**
 * GetRisCustom01R1InfoItem(+ Machines/Materials) → 화면 공정 1건
 */
export const MapGetRisCustom01R1InfoItemToProcess = (row, index) => {
  if (!row || typeof row !== 'object') return null;
  const machines = P(row, 'machines', 'Machines') || [];
  const materials = P(row, 'materials', 'Materials') || [];

  const equipmentList = (Array.isArray(machines) ? machines : []).map((m) => ({
    id: String(P(m, 'machineId', 'MachineId') ?? ''),
    name: String(P(m, 'machineNm', 'MachineNm') ?? ''),
    machineType: String(P(m, 'machineType', 'MachineType') ?? '0'),
    mapId: P(m, 'mapId', 'MapId'),
    sriId: P(m, 'sriId', 'SriId'),
  }));

  const materialList = (Array.isArray(materials) ? materials : []).map((m) => ({
    id: String(P(m, 'hbdId', 'HbdId') ?? ''),
    name: String(
      P(m, 'substanceName', 'SubstanceName') ||
        P(m, 'productName', 'ProductName') ||
        ''
    ),
    casNo: String(P(m, 'casNo', 'CasNo') ?? ''),
    hbmId: P(m, 'hbmId', 'HbmId'),
    productName: String(P(m, 'productName', 'ProductName') ?? ''),
    mapId: P(m, 'mapId', 'MapId'),
    sriId: P(m, 'sriId', 'SriId'),
  }));

  return {
    sriId: P(row, 'sriId', 'SriId'),
    processId: String(P(row, 'gongJongCd', 'GongJongCd') ?? ''),
    processNm: String(P(row, 'gongJongNm', 'GongJongNm') ?? ''),
    processNo: Number(P(row, 'no', 'No')) || index + 1,
    selected: index === 0,
    depCd: P(row, 'depCd', 'DepCd') ?? '',
    depNm: P(row, 'depNm', 'DepNm') ?? '',
    accidentCnt: P(row, 'accidentCnt', 'AccidentCnt'),
    nearMissCnt: P(row, 'nearMissCnt', 'NearMissCnt'),
    workerTrait: P(row, 'workerTrait', 'WorkerTrait') ?? '',
    shiftType: P(row, 'shiftType', 'ShiftType') ?? 'N',
    handlingWeight: P(row, 'handlingWeight', 'HandlingWeight') ?? '',
    handlingForm: P(row, 'handlingForm', 'HandlingForm') ?? '',
    transportMethod: P(row, 'transportMethod', 'TransportMethod') ?? '',
    transportMethodTxt:
      P(row, 'transportMethodTxt', 'TransportMethodTxt') ?? '',
    healthExamYn: P(row, 'healthExamYn', 'HealthExamYn'),
    isSafetyWorkPermit:
      P(row, 'isSafetyWorkPermit', 'IsSafetyWorkPermit') ?? 'N',
    isEnvironmentMeasurement: P(
      row,
      'isEnvironmentMeasurement',
      'IsEnvironmentMeasurement'
    ),
    environmentMeasurement:
      P(row, 'environmentMeasurement', 'EnvironmentMeasurement') ?? '',
    exposureStandards: P(row, 'exposureStandards', 'ExposureStandards') ?? '',
    isSpecialSafetyTraining: P(
      row,
      'isSpecialSafetyTraining',
      'IsSpecialSafetyTraining'
    ),
    shiftTypeTxt: P(row, 'shiftTypeTxt', 'ShiftTypeTxt') ?? '',
    maxRiskGrade: P(row, 'maxRiskGrade', 'MaxRiskGrade'),
    maxRiskScore: P(row, 'maxRiskScore', 'MaxRiskScore'),
    equipmentList,
    materialList,
  };
};

/**
 * GetRisCustom01R2 응답 → ProcessSelectModal용 depData / processDataByDept
 */
export const MapGetRisCustom01R2ResponseToProcessModal = (response) => {
  const empty = { depData: [], processDataByDept: {} };
  if (!response || typeof response !== 'object') return empty;

  const ok = response.isSuccess !== false && response.IsSuccess !== false;
  if (!ok) return empty;

  const dataArr = response.data ?? response.Data;
  if (!Array.isArray(dataArr) || dataArr.length === 0) return empty;

  const head = dataArr[0];
  const depLastList = P(head, 'depLastList', 'DepLastList') ?? [];
  const gongjongList = P(head, 'gongjongList', 'GongjongList') ?? [];

  const depData = (Array.isArray(depLastList) ? depLastList : []).map(
    (item) => ({
      value: NormalizeDepCdKey(P(item, 'depCd', 'DepCd')),
      label: String(
        P(item, 'depNmLast', 'DepNmLast') ?? P(item, 'depNm', 'DepNm') ?? ''
      ),
    })
  );

  const processDataByDept = {};
  for (const g of Array.isArray(gongjongList) ? gongjongList : []) {
    const row = MapGongjongRowToProcessItem(g);
    if (!row) continue;
    if (!processDataByDept[row.depCd]) processDataByDept[row.depCd] = [];
    processDataByDept[row.depCd].push(row);
  }

  return { depData, processDataByDept };
};

/**
 * Regulation(Step00) 저장값과 동일 소스 — SafetyInfo.js Select 옵션 문자열로 맞춤
 * (Regulation: "위험성 수준 3단계판단법" → SafetyInfo: "위험성 수준 3단계 판단법")
 */
export const MapRsMethodForSafetyInfoForm = (v) => {
  const reg = NormalizeRsMethodForForm(v);
  if (reg === '위험성 수준 3단계판단법') return '위험성 수준 3단계 판단법';
  return reg;
};

/**
 * GetRisCustomR1 RiskMasterList 항목 → Step01 기본평가정보(실시규정 SaveRisCustomU1)
 */
export const MapRiskMasterRowToSafetyInfoBasicEval = (item, options = {}) => {
  const { assessmentTitle = '' } = options;
  if (!item || typeof item !== 'object') return null;
  const rsStart =
    String(item.RsStartDate ?? item.rsStartDate ?? '').slice(0, 10) || '';
  const rsEnd =
    String(item.RsEndDate ?? item.rsEndDate ?? '').slice(0, 10) || '';
  const nm =
    String(item.RsNm ?? item.rsNm ?? '').trim() ||
    String(assessmentTitle ?? '').trim() ||
    '';
  return {
    rsNm: nm,
    rsGubun: NormalizeRsGubunForForm(item.RsGubun ?? item.rsGubun),
    rsMethod: MapRsMethodForSafetyInfoForm(item.RsMethod ?? item.rsMethod),
    depNm: String(item.DepNm ?? item.depNm ?? '').trim(),
    rsStartDate: rsStart,
    rsEndDate: rsEnd,
  };
};

/**
 * GetRisCustom01R1 API 응답(SaveRisBasicResult + Data: 공정 목록) → SafetyInfo 폼 패치
 */
export const MapGetRisCustom01R1ResponseToForm = (response, options = {}) => {
  const { assessmentTitle = '' } = options;
  if (!response || typeof response !== 'object') return null;

  const ok = response.isSuccess !== false && response.IsSuccess !== false;
  if (!ok) return null;

  const items = response.data ?? response.Data;
  if (!Array.isArray(items)) return null;

  const firstRawRow = items.length > 0 ? items[0] : null;
  const riskMasterList =
    firstRawRow?.riskMasterList ?? firstRawRow?.RiskMasterList ?? [];
  const firstRiskMaster =
    Array.isArray(riskMasterList) && riskMasterList.length > 0
      ? riskMasterList[0]
      : null;
  const rsStartDate =
    NormalizeRsDateForInput(P(response, 'rsStartDate', 'RsStartDate')) ||
    NormalizeRsDateForInput(P(firstRawRow, 'rsStartDate', 'RsStartDate')) ||
    NormalizeRsDateForInput(P(firstRiskMaster, 'rsStartDate', 'RsStartDate'));
  const rsEndDate =
    NormalizeRsDateForInput(P(response, 'rsEndDate', 'RsEndDate')) ||
    NormalizeRsDateForInput(P(firstRawRow, 'rsEndDate', 'RsEndDate')) ||
    NormalizeRsDateForInput(P(firstRiskMaster, 'rsEndDate', 'RsEndDate'));

  const processList = items
    .map((row, idx) => MapGetRisCustom01R1InfoItemToProcess(row, idx))
    .filter(Boolean);

  /** 평가 부서(depNm)는 GetRisCustomR1 마스터(실시규정)에서만 채움 — 공정 목록 dep와 혼동 방지 */

  return {
    rsNm: assessmentTitle || '',
    processList,
    equipmentList: processList[0]?.equipmentList ?? [],
    materialList: processList[0]?.materialList ?? [],
    rsStartDate,
    rsEndDate,
  };
};

/**
 * GetRisCustom01R4 — Data[0]: GongJongInfoList + RiskDetailList → SafetyInfo 폼 패치
 * (공정·설비·물질은 GetRisCustom01R1과 동일 항목)
 */
export const MapGetRisCustom01R4ResponseToForm = (response, options = {}) => {
  const { assessmentTitle = '' } = options;
  if (!response || typeof response !== 'object') return null;

  const ok = response.isSuccess !== false && response.IsSuccess !== false;
  if (!ok) return null;

  const dataArr = response.data ?? response.Data;
  if (!Array.isArray(dataArr) || dataArr.length === 0) return null;

  const head = dataArr[0];
  const gongList = head?.gongJongInfoList ?? head?.GongJongInfoList ?? [];
  if (!Array.isArray(gongList)) return null;

  const processList = gongList
    .map((row, idx) => MapGetRisCustom01R1InfoItemToProcess(row, idx))
    .filter(Boolean);

  const riskMasterList = head?.riskMasterList ?? head?.RiskMasterList ?? [];
  const firstRiskMaster =
    Array.isArray(riskMasterList) && riskMasterList.length > 0
      ? riskMasterList[0]
      : null;

  const rsStartDate =
    NormalizeRsDateForInput(P(response, 'rsStartDate', 'RsStartDate')) ||
    NormalizeRsDateForInput(P(head, 'rsStartDate', 'RsStartDate')) ||
    NormalizeRsDateForInput(P(firstRiskMaster, 'rsStartDate', 'RsStartDate'));
  const rsEndDate =
    NormalizeRsDateForInput(P(response, 'rsEndDate', 'RsEndDate')) ||
    NormalizeRsDateForInput(P(head, 'rsEndDate', 'RsEndDate')) ||
    NormalizeRsDateForInput(P(firstRiskMaster, 'rsEndDate', 'RsEndDate'));

  /** 평가 부서는 실시규정 마스터 동기화(SafetyInfoF)에서만 설정 */

  return {
    rsNm: assessmentTitle || '',
    processList,
    equipmentList: processList[0]?.equipmentList ?? [],
    materialList: processList[0]?.materialList ?? [],
    rsStartDate,
    rsEndDate,
  };
};

/** GetRisCustom01R4 응답에서 RiskDetailList 평탄 추출 (GetRisCustom02R1Item[]) */
export const ExtractGetRisCustom01R4RiskDetailList = (response) => {
  if (!response || typeof response !== 'object') return [];
  const dataArr = response.data ?? response.Data;
  if (!Array.isArray(dataArr) || dataArr.length === 0) return [];
  const head = dataArr[0];
  const list = head?.riskDetailList ?? head?.RiskDetailList ?? [];
  return Array.isArray(list) ? list : [];
};

export const GetSafetyInfoDefaultForm = () => ({
  rsNm: '',
  rsGubun: '최초평가',
  rsMethod: '위험성 수준 3단계 판단법',
  depNm: '본부, 파트',
  rsStartDate: dayjs().format('YYYY-MM-DD'),
  rsEndDate: dayjs().format('YYYY-MM-DD'),
  processList: [
    { processId: 'P001', processNm: '공정이다1', processNo: 1, selected: true },
    {
      processId: 'P002',
      processNm: '공정이다2',
      processNo: 2,
      selected: false,
    },
  ],
  equipmentList: [{ id: 'E001', name: '유압 프레스 1호기' }],
  materialList: [
    { id: 'M001', name: '윤활유 (Oil-202)', casNo: '123-45-6' },
    { id: 'M002', name: '세척제 (Clean-X)', casNo: '987-65-4' },
  ],
});

export default SafetyInfo_Schema;
